import { create } from 'zustand';
import {
  type ClientMessage,
  type EntityRef,
  type EntityType,
  type Message,
  type MessageMention,
  type ReactionGroup,
  type ThreadLanguage,
  type TranslationResult,
  ORIGINAL_LANGUAGE,
  AI_TRANSLATE_POLL_MS,
  AI_TRANSLATE_POLL_MAX,
  normalizeEntityKeyClient,
  makeOptimistic,
  upsertMessage,
  markFailed,
  applyEdited,
  applyDeleted,
  applyReactions,
} from '@atlas/shared';
import { messagingApi } from '@/services/messaging.api';
import { translationApi } from '@/services/translation.api';

export interface ThreadState {
  entityRef: EntityRef;
  threadId: string | null;
  messages: ClientMessage[];
  unreadCount: number;
  lastReadAt: string | null;
  lastReadMessageId: string | null;
  nextBeforeCursor: string | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  /** Target language for the whole thread; 'original' = show source, no translation. */
  language: ThreadLanguage;
  /** Per-message translation cache keyed by `${messageId}:${targetLanguage}`. */
  translations: Record<string, TranslationResult>;
}

interface ChatState {
  threads: Record<string, ThreadState>;
  load: (ref: EntityRef) => Promise<void>;
  loadOlder: (ref: EntityRef) => Promise<void>;
  send: (ref: EntityRef, author: { userId: string; name: string }, body: string, mentions: MessageMention[]) => Promise<void>;
  retry: (ref: EntityRef, author: { userId: string; name: string }, tempId: string) => Promise<void>;
  edit: (ref: EntityRef, messageId: string, body: string, mentions: MessageMention[]) => Promise<boolean>;
  remove: (ref: EntityRef, messageId: string) => Promise<void>;
  react: (ref: EntityRef, messageId: string, emoji: string) => Promise<void>;
  markRead: (ref: EntityRef, messageId: string) => Promise<void>;
  receiveNew: (entityKey: string, message: Message, tempId?: string) => void;
  receiveEdited: (entityKey: string, ev: { messageId: string; body: string; mentions: MessageMention[]; editedAt: string }) => void;
  receiveDeleted: (entityKey: string, ev: { messageId: string; deletedAt: string }) => void;
  receiveReaction: (entityKey: string, ev: { messageId: string; reactions: ReactionGroup[] }) => void;
  setThreadLanguage: (ref: EntityRef, language: ThreadLanguage) => void;
  ensureTranslation: (ref: EntityRef, messageId: string, opts?: { force?: boolean }) => Promise<void>;
}

const keyOf = (ref: EntityRef) => normalizeEntityKeyClient(ref.type, ref.id);

const EMPTY: Omit<ThreadState, 'entityRef'> = {
  threadId: null,
  messages: [],
  unreadCount: 0,
  lastReadAt: null,
  lastReadMessageId: null,
  nextBeforeCursor: null,
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  error: null,
  language: ORIGINAL_LANGUAGE,
  translations: {},
};

let tempCounter = 0;
const nextTempId = () => `tmp-${Date.now()}-${tempCounter++}`;

export const useChatStore = create<ChatState>((set, get) => {
  const patch = (key: string, ref: EntityRef, fn: (t: ThreadState) => ThreadState) =>
    set((s) => {
      const cur = s.threads[key] ?? { entityRef: ref, ...EMPTY };
      return { threads: { ...s.threads, [key]: fn(cur) } };
    });

  return {
    threads: {},

    load: async (ref) => {
      const key = keyOf(ref);
      patch(key, ref, (t) => ({ ...t, isLoading: true, error: null }));
      try {
        const data = await messagingApi.listMessages(ref.type as EntityType, ref.id, { limit: 50 });
        patch(key, ref, (t) => ({
          ...t,
          threadId: data.thread._id,
          messages: data.messages.map((m) => ({ ...m, status: 'sent' as const })),
          unreadCount: data.thread.unreadCount,
          lastReadAt: data.thread.lastReadAt,
          lastReadMessageId: data.thread.lastReadMessageId,
          nextBeforeCursor: data.nextBeforeCursor,
          hasMore: data.nextBeforeCursor !== null,
          isLoading: false,
        }));
      } catch (e) {
        patch(key, ref, (t) => ({ ...t, isLoading: false, error: (e as Error).message }));
      }
    },

    loadOlder: async (ref) => {
      const key = keyOf(ref);
      const t = get().threads[key];
      if (!t || !t.hasMore || t.isLoadingMore || !t.nextBeforeCursor) return;
      patch(key, ref, (s) => ({ ...s, isLoadingMore: true }));
      try {
        const data = await messagingApi.listMessages(ref.type as EntityType, ref.id, {
          before: t.nextBeforeCursor, limit: 50,
        });
        const older = data.messages.map((m) => ({ ...m, status: 'sent' as const }));
        patch(key, ref, (s) => ({
          ...s,
          messages: [...older, ...s.messages],
          nextBeforeCursor: data.nextBeforeCursor,
          hasMore: data.nextBeforeCursor !== null,
          isLoadingMore: false,
        }));
      } catch (e) {
        patch(key, ref, (s) => ({ ...s, isLoadingMore: false, error: (e as Error).message }));
      }
    },

    send: async (ref, author, body, mentions) => {
      const key = keyOf(ref);
      const tempId = nextTempId();
      const optimistic = makeOptimistic({
        tempId,
        authorUserId: author.userId,
        authorDisplayName: author.name,
        body,
        mentions,
        createdAt: new Date().toISOString(),
      });
      patch(key, ref, (t) => ({ ...t, messages: [...t.messages, optimistic] }));
      try {
        const res = await messagingApi.postMessage(ref.type as EntityType, ref.id, { body, mentions, tempId });
        patch(key, ref, (t) => ({
          ...t,
          threadId: t.threadId ?? res.message.threadId,
          messages: upsertMessage(t.messages, res.message, tempId),
        }));
      } catch {
        patch(key, ref, (t) => ({ ...t, messages: markFailed(t.messages, tempId) }));
      }
    },

    retry: async (ref, _author, tempId) => {
      const key = keyOf(ref);
      const t = get().threads[key];
      const failed = t?.messages.find((m) => m.tempId === tempId && m._id === null);
      if (!failed) return;
      patch(key, ref, (s) => ({
        ...s,
        messages: s.messages.map((m) => (m.tempId === tempId ? { ...m, status: 'sending' } : m)),
      }));
      try {
        const res = await messagingApi.postMessage(ref.type as EntityType, ref.id, {
          body: failed.body, mentions: failed.mentions, tempId,
        });
        patch(key, ref, (s) => ({ ...s, messages: upsertMessage(s.messages, res.message, tempId) }));
      } catch {
        patch(key, ref, (s) => ({ ...s, messages: markFailed(s.messages, tempId) }));
      }
    },

    edit: async (ref, messageId, body, mentions) => {
      const key = keyOf(ref);
      try {
        const updated = await messagingApi.editMessage(messageId, { body, mentions });
        patch(key, ref, (t) => ({
          ...t,
          error: null,
          messages: applyEdited(t.messages, {
            messageId, body: updated.body, mentions: updated.mentions, editedAt: updated.editedAt ?? new Date().toISOString(),
          }),
        }));
        return true;
      } catch (e) {
        patch(key, ref, (t) => ({ ...t, error: (e as Error).message }));
        return false;
      }
    },

    remove: async (ref, messageId) => {
      const key = keyOf(ref);
      await messagingApi.deleteMessage(messageId);
      patch(key, ref, (t) => ({
        ...t,
        messages: applyDeleted(t.messages, { messageId, deletedAt: new Date().toISOString() }),
      }));
    },

    react: async (ref, messageId, emoji) => {
      const key = keyOf(ref);
      const res = await messagingApi.toggleReaction(messageId, emoji);
      patch(key, ref, (t) => ({
        ...t,
        messages: applyReactions(t.messages, { messageId, reactions: res.reactions }),
      }));
    },

    markRead: async (ref, messageId) => {
      const key = keyOf(ref);
      const t = get().threads[key];
      if (!messageId || messageId === t?.lastReadMessageId) return;
      try {
        await messagingApi.markRead(ref.type as EntityType, ref.id, messageId);
        patch(key, ref, (s) => ({ ...s, unreadCount: 0, lastReadMessageId: messageId }));
      } catch {
        /* best-effort; next visible-bottom will retry */
      }
    },

    setThreadLanguage: (ref, language) => {
      const key = keyOf(ref);
      patch(key, ref, (t) => ({ ...t, language }));
      // Per-message translation is driven by MessageItem's effect when `language`
      // changes — it knows which messages are the viewer's own (never translated).
    },

    ensureTranslation: async (ref, messageId, opts) => {
      const key = keyOf(ref);
      const t = get().threads[key];
      if (!t || t.language === ORIGINAL_LANGUAGE) return;
      const target = t.language;
      const cacheKey = `${messageId}:${target}`;
      const cached = t.translations[cacheKey];
      if (cached?.status === 'completed') return;
      if (cached?.status === 'failed' && !opts?.force) return;

      const store = (r: TranslationResult) =>
        patch(key, ref, (s) => ({ ...s, translations: { ...s.translations, [cacheKey]: r } }));

      for (let attempt = 0; attempt < AI_TRANSLATE_POLL_MAX; attempt++) {
        let res: TranslationResult;
        try {
          res = await translationApi.translate(messageId, target);
        } catch {
          if (get().threads[key]?.language !== target) return;
          store({ messageId, targetLanguage: target, status: 'failed', errorCode: 'AI_UNAVAILABLE' });
          return;
        }
        // The thread language may have changed while we were awaiting — bail if so.
        if (get().threads[key]?.language !== target) return;
        store(res);
        if (res.status !== 'pending') return;
        await new Promise((r) => setTimeout(r, AI_TRANSLATE_POLL_MS));
      }
      // Exhausted polling while still pending — write a terminal failure so the UI
      // can show a retry instead of a perpetual spinner.
      if (get().threads[key]?.language === target) {
        store({ messageId, targetLanguage: target, status: 'failed', errorCode: 'AI_TIMEOUT' });
      }
    },

    receiveNew: (entityKey, message, tempId) =>
      set((s) => {
        const t = s.threads[entityKey];
        if (!t) return s;
        const isMine = !!tempId && t.messages.some((m) => m.tempId === tempId);
        const messages = upsertMessage(t.messages, message, tempId);
        const grew = messages.length > t.messages.length;
        return {
          threads: {
            ...s.threads,
            [entityKey]: {
              ...t,
              threadId: t.threadId ?? message.threadId,
              messages,
              unreadCount: grew && !isMine ? t.unreadCount + 1 : t.unreadCount,
            },
          },
        };
      }),

    receiveEdited: (entityKey, ev) =>
      set((s) => {
        const t = s.threads[entityKey];
        if (!t) return s;
        return { threads: { ...s.threads, [entityKey]: { ...t, messages: applyEdited(t.messages, ev) } } };
      }),

    receiveDeleted: (entityKey, ev) =>
      set((s) => {
        const t = s.threads[entityKey];
        if (!t) return s;
        return { threads: { ...s.threads, [entityKey]: { ...t, messages: applyDeleted(t.messages, ev) } } };
      }),

    receiveReaction: (entityKey, ev) =>
      set((s) => {
        const t = s.threads[entityKey];
        if (!t) return s;
        return { threads: { ...s.threads, [entityKey]: { ...t, messages: applyReactions(t.messages, ev) } } };
      }),
  };
});
